import React, { useState } from "react";
import "./createitinerarydialog.css";
import { Plus, X, Users, MapPin, Calendar, FileText, ChevronRight, Plane, Hotel, Phone, Loader } from "lucide-react";

export default function CreateItineraryDialog({
  open,
  onClose,
  onSubmit,
  isLoading = false
}) {
  // Initial state with all necessary fields
  const [formData, setFormData] = useState({
    title: "",
    preparedFor: [""],
    startDate: "",
    endDate: "",
    totalDays: "",
    destinations: [{ city: "", country: "", description: "" }],
    notes: "",
    status: "Draft",
    
    // Days section
    days: [{
      dayNumber: 1,
      date: "",
      city: "",
      title: "",
      description: "",
      activities: [{
        name: "",
        startTime: "",
        endTime: "",
        description: "",
        guideName: "",
        guidePhone: ""
      }],
      mealsIncluded: [],
      inclusions: [],
      exclusions: []
    }],
    
    // Flights section
    flights: [{
      flightNumber: "",
      airline: "",
      operatedBy: "",
      fromCity: "",
      toCity: "",
      departureDate: "",
      departureTime: "",
      arrivalDate: "",
      arrivalTime: ""
    }],
    
    // Hotels section
    hotels: [{
      name: "",
      address: "",
      city: "",
      country: "",
      phone: "",
      images: [],
      roomDetails: [{
        roomType: "",
        nights: ""
      }]
    }],
    
    // Local Contacts section
    localContacts: [{
      name: "",
      company: "",
      role: "",
      phone: "",
      email: "",
      address: ""
    }]
  });

  const [errors, setErrors] = useState({});
  const [activeStep, setActiveStep] = useState(1);
  const [mealsOptions] = useState(["Breakfast", "Lunch", "Dinner"]);

  if (!open) return null;

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.title.trim()) newErrors.title = "Title is required";
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.endDate) newErrors.endDate = "End date is required";
      
      if (formData.startDate && formData.endDate) {
        const start = new Date(formData.startDate);
        const end = new Date(formData.endDate);
        if (end < start) newErrors.endDate = "End date must be after start date";
      }
    }
    
    if (step === 2) {
      const validTravelers = formData.preparedFor.filter(t => t.trim() !== "");
      if (validTravelers.length === 0) {
        newErrors.preparedFor = "At least one traveler is required";
      }
    }
    
    if (step === 3) {
      const validDestinations = formData.destinations.filter(d => 
        d.city.trim() !== "" && d.country.trim() !== ""
      );
      if (validDestinations.length === 0) {
        newErrors.destinations = "At least one destination with city and country is required";
      }
    }
    
    // Validate all steps before submission
    if (step === 6) {
      if (!formData.title.trim()) newErrors.title = "Title is required";
      if (!formData.startDate) newErrors.startDate = "Start date is required";
      if (!formData.endDate) newErrors.endDate = "End date is required";
      
      const validTravelers = formData.preparedFor.filter(t => t.trim() !== "");
      if (validTravelers.length === 0) {
        newErrors.preparedFor = "At least one traveler is required";
      }
      
      const validDestinations = formData.destinations.filter(d => 
        d.city.trim() !== "" && d.country.trim() !== ""
      );
      if (validDestinations.length === 0) {
        newErrors.destinations = "At least one destination with city and country is required";
      }
    }
    
    return newErrors;
  };

  const handleNextStep = () => {
    const stepErrors = validateStep(activeStep);
    if (Object.keys(stepErrors).length === 0) {
      setActiveStep(prev => Math.min(prev + 1, 6));
      setErrors({});
    } else {
      setErrors(stepErrors);
    }
  };

  const handlePrevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 1));
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleTravelerChange = (index, value) => {
    const newTravelers = [...formData.preparedFor];
    newTravelers[index] = value;
    setFormData({ ...formData, preparedFor: newTravelers });
    
    if (errors.preparedFor) {
      setErrors({ ...errors, preparedFor: "" });
    }
  };

  const addTraveler = () => {
    setFormData({
      ...formData,
      preparedFor: [...formData.preparedFor, ""]
    });
  };

  const removeTraveler = (index) => {
    if (formData.preparedFor.length > 1) {
      const newTravelers = formData.preparedFor.filter((_, i) => i !== index);
      setFormData({ ...formData, preparedFor: newTravelers });
    }
  };

  const handleDestinationChange = (index, field, value) => {
    const newDestinations = [...formData.destinations];
    newDestinations[index][field] = value;
    setFormData({ ...formData, destinations: newDestinations });
    
    if (errors.destinations) {
      setErrors({ ...errors, destinations: "" });
    }
  };

  const addDestination = () => {
    setFormData({
      ...formData,
      destinations: [...formData.destinations, { city: "", country: "", description: "" }]
    });
  };

  const removeDestination = (index) => {
    if (formData.destinations.length > 1) {
      const newDestinations = formData.destinations.filter((_, i) => i !== index);
      setFormData({ ...formData, destinations: newDestinations });
    }
  };

  // Days Handlers
  const handleDayChange = (dayIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex][field] = value;
    setFormData({ ...formData, days: newDays });
  };

  const handleActivityChange = (dayIndex, activityIndex, field, value) => {
    const newDays = [...formData.days];
    newDays[dayIndex].activities[activityIndex][field] = value;
    setFormData({ ...formData, days: newDays });
  };

  const addDay = () => {
    const newDayNumber = formData.days.length + 1;
    setFormData({
      ...formData,
      days: [...formData.days, {
        dayNumber: newDayNumber,
        date: "",
        city: "",
        title: "",
        description: "",
        activities: [{
          name: "",
          startTime: "",
          endTime: "",
          description: "",
          guideName: "",
          guidePhone: ""
        }],
        mealsIncluded: [],
        inclusions: [],
        exclusions: []
      }]
    });
  };

  const removeDay = (index) => {
    if (formData.days.length > 1) {
      const newDays = formData.days.filter((_, i) => i !== index);
      // Re-number days after removal
      const renumberedDays = newDays.map((day, idx) => ({
        ...day,
        dayNumber: idx + 1
      }));
      setFormData({ ...formData, days: renumberedDays });
    }
  };

  const addActivity = (dayIndex) => {
    const newDays = [...formData.days];
    newDays[dayIndex].activities.push({
      name: "",
      startTime: "",
      endTime: "",
      description: "",
      guideName: "",
      guidePhone: ""
    });
    setFormData({ ...formData, days: newDays });
  };

  const removeActivity = (dayIndex, activityIndex) => {
    const newDays = [...formData.days];
    if (newDays[dayIndex].activities.length > 1) {
      newDays[dayIndex].activities.splice(activityIndex, 1);
      setFormData({ ...formData, days: newDays });
    }
  };

  const handleMealToggle = (dayIndex, meal) => {
    const newDays = [...formData.days];
    const currentMeals = newDays[dayIndex].mealsIncluded;
    
    if (currentMeals.includes(meal)) {
      newDays[dayIndex].mealsIncluded = currentMeals.filter(m => m !== meal);
    } else {
      newDays[dayIndex].mealsIncluded = [...currentMeals, meal];
    }
    
    setFormData({ ...formData, days: newDays });
  };

  // Flight Handlers
  const handleFlightChange = (index, field, value) => {
    const newFlights = [...formData.flights];
    newFlights[index][field] = value;
    setFormData({ ...formData, flights: newFlights });
  };

  const addFlight = () => {
    setFormData({
      ...formData,
      flights: [...formData.flights, {
        flightNumber: "",
        airline: "",
        operatedBy: "",
        fromCity: "",
        toCity: "",
        departureDate: "",
        departureTime: "",
        arrivalDate: "",
        arrivalTime: ""
      }]
    });
  };

  const removeFlight = (index) => {
    if (formData.flights.length > 1) {
      const newFlights = formData.flights.filter((_, i) => i !== index);
      setFormData({ ...formData, flights: newFlights });
    }
  };

  // Hotel Handlers
  const handleHotelChange = (index, field, value) => {
    const newHotels = [...formData.hotels];
    newHotels[index][field] = value;
    setFormData({ ...formData, hotels: newHotels });
  };

  const handleRoomDetailChange = (hotelIndex, roomIndex, field, value) => {
    const newHotels = [...formData.hotels];
    newHotels[hotelIndex].roomDetails[roomIndex][field] = value;
    setFormData({ ...formData, hotels: newHotels });
  };

  const addHotel = () => {
    setFormData({
      ...formData,
      hotels: [...formData.hotels, {
        name: "",
        address: "",
        city: "",
        country: "",
        phone: "",
        images: [],
        roomDetails: [{
          roomType: "",
          nights: ""
        }]
      }]
    });
  };

  const removeHotel = (index) => {
    if (formData.hotels.length > 1) {
      const newHotels = formData.hotels.filter((_, i) => i !== index);
      setFormData({ ...formData, hotels: newHotels });
    }
  };

  const addRoomDetail = (hotelIndex) => {
    const newHotels = [...formData.hotels];
    newHotels[hotelIndex].roomDetails.push({
      roomType: "",
      nights: ""
    });
    setFormData({ ...formData, hotels: newHotels });
  };

  const removeRoomDetail = (hotelIndex, roomIndex) => {
    const newHotels = [...formData.hotels];
    if (newHotels[hotelIndex].roomDetails.length > 1) {
      newHotels[hotelIndex].roomDetails.splice(roomIndex, 1);
      setFormData({ ...formData, hotels: newHotels });
    }
  };

  const handleImageUpload = (hotelIndex, e) => {
    const files = Array.from(e.target.files);
    const newHotels = [...formData.hotels];
    // In a real implementation, you would upload files to server
    // For now, we'll just store file names
    newHotels[hotelIndex].images = [...newHotels[hotelIndex].images, ...files.map(f => f.name)];
    setFormData({ ...formData, hotels: newHotels });
  };

  // Local Contact Handlers
  const handleContactChange = (index, field, value) => {
    const newContacts = [...formData.localContacts];
    newContacts[index][field] = value;
    setFormData({ ...formData, localContacts: newContacts });
  };

  const addContact = () => {
    setFormData({
      ...formData,
      localContacts: [...formData.localContacts, {
        name: "",
        company: "",
        role: "",
        phone: "",
        email: "",
        address: ""
      }]
    });
  };

  const removeContact = (index) => {
    if (formData.localContacts.length > 1) {
      const newContacts = formData.localContacts.filter((_, i) => i !== index);
      setFormData({ ...formData, localContacts: newContacts });
    }
  };


  const calculateTotalDays = () => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, totalDays: diffDays }));
      
      // Auto-populate days only if days array is empty or needs adjustment
      if (!formData.days.length || formData.days.length !== diffDays) {
        const daysArray = [];
        for (let i = 1; i <= diffDays; i++) {
          const dayDate = new Date(start);
          dayDate.setDate(start.getDate() + (i - 1));
          
          // Preserve existing day data if available
          const existingDay = formData.days[i - 1] || {};
          
          daysArray.push({
            dayNumber: i,
            date: dayDate.toISOString().split('T')[0],
            city: existingDay.city || "",
            title: existingDay.title || "",
            description: existingDay.description || "",
            activities: existingDay.activities || [{
              name: "",
              startTime: "",
              endTime: "",
              description: "",
              guideName: "",
              guidePhone: ""
            }],
            mealsIncluded: existingDay.mealsIncluded || [],
            inclusions: existingDay.inclusions || [],
            exclusions: existingDay.exclusions || []
          });
        }
        setFormData(prev => ({ ...prev, days: daysArray }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate all required fields before submission
    const finalValidation = validateStep(6);
    if (Object.keys(finalValidation).length > 0) {
      setErrors(finalValidation);
      setActiveStep(1); // Go back to first step to show errors
      return;
    }
    
    // Filter out empty data
    const filteredTravelers = formData.preparedFor.filter(name => name.trim() !== "");
    const filteredDestinations = formData.destinations.filter(
      dest => dest.city.trim() !== "" && dest.country.trim() !== ""
    );
    
    // Don't filter days, flights, hotels, or contacts - send all
    // The backend will handle empty objects
    const filteredDays = formData.days.map(day => ({
      ...day,
      // Ensure arrays are present even if empty
      activities: day.activities || [],
      mealsIncluded: day.mealsIncluded || [],
      inclusions: day.inclusions || [],
      exclusions: day.exclusions || []
    }));
    
    const filteredFlights = formData.flights.map(flight => ({
      ...flight,
      // Ensure all fields have at least empty strings
      flightNumber: flight.flightNumber || "",
      airline: flight.airline || "",
      operatedBy: flight.operatedBy || "",
      fromCity: flight.fromCity || "",
      toCity: flight.toCity || ""
    }));
    
    const filteredHotels = formData.hotels.map(hotel => ({
      ...hotel,
      // Ensure all fields have at least empty strings
      name: hotel.name || "",
      address: hotel.address || "",
      city: hotel.city || "",
      country: hotel.country || "",
      phone: hotel.phone || "",
      images: hotel.images || [],
      roomDetails: hotel.roomDetails || []
    }));
    
    const filteredContacts = formData.localContacts.map(contact => ({
      ...contact,
      // Ensure all fields have at least empty strings
      name: contact.name || "",
      company: contact.company || "",
      role: contact.role || "",
      phone: contact.phone || "",
      email: contact.email || "",
      address: contact.address || ""
    }));
    
    const submissionData = {
      title: formData.title,
      preparedFor: filteredTravelers,
      startDate: formData.startDate,
      endDate: formData.endDate,
      totalDays: formData.totalDays || 1,
      destinations: filteredDestinations,
      notes: formData.notes,
      status: formData.status,
      days: filteredDays,
      flights: filteredFlights,
      hotels: filteredHotels,
      localContacts: filteredContacts
    };
    
    console.log("Submitting itinerary data:", submissionData);
    onSubmit(submissionData);
  };

  const steps = [
    { number: 1, title: "Basic Info", icon: <FileText size={18} /> },
    { number: 2, title: "Travelers", icon: <Users size={18} /> },
    { number: 3, title: "Destinations", icon: <MapPin size={18} /> },
    { number: 4, title: "Days", icon: <Calendar size={18} /> },
    { number: 5, title: "Flights & Hotels", icon: <Plane size={18} /> },
    { number: 6, title: "Review", icon: <FileText size={18} /> }
  ];

  return (
    <div className="dialog-backdrop">
      <div className="dialog-container">
        <div className="dialog-header">
          <div>
            <h2>Create New Itinerary</h2>
            <p className="dialog-subtitle">Fill in the details to create a new travel itinerary</p>
          </div>
          <button 
            onClick={onClose} 
            className="close-btn"
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="progress-steps">
          {steps.map((step, index) => (
            <div key={step.number} className="step-container">
              <div className={`step ${activeStep === step.number ? 'active' : ''} ${activeStep > step.number ? 'completed' : ''}`}>
                <div className="step-icon">
                  {activeStep > step.number ? (
                    <div className="step-check">✓</div>
                  ) : (
                    step.icon
                  )}
                </div>
                <span className="step-title">{step.title}</span>
              </div>
              {index < steps.length - 1 && (
                <div className={`step-line ${activeStep > step.number ? 'completed' : ''}`}></div>
              )}
            </div>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="dialog-form">
          {/* Step 1: Basic Information */}
          {activeStep === 1 && (
            <div className="form-step">
              <h3 className="step-heading">Basic Information</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>
                    Itinerary Title <span className="required">*</span>
                  </label>
                  <input
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="e.g., Portugal & Spain Tour"
                    className={errors.title ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.title && <span className="error-text">{errors.title}</span>}
                </div>

                <div className="form-group">
                  <label>
                    Start Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={(e) => {
                      handleChange(e);
                      calculateTotalDays();
                    }}
                    className={errors.startDate ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.startDate && <span className="error-text">{errors.startDate}</span>}
                </div>

                <div className="form-group">
                  <label>
                    End Date <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={(e) => {
                      handleChange(e);
                      calculateTotalDays();
                    }}
                    className={errors.endDate ? "error" : ""}
                    disabled={isLoading}
                  />
                  {errors.endDate && <span className="error-text">{errors.endDate}</span>}
                </div>

                <div className="form-group">
                  <label>Total Days</label>
                  <div className="total-days-display">
                    <input
                      type="number"
                      name="totalDays"
                      value={formData.totalDays}
                      readOnly
                      className="read-only"
                      disabled={isLoading}
                    />
                    <span className="days-label">days</span>
                  </div>
                </div>

                <div className="form-group full-width">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={isLoading}
                  >
                    <option value="Draft">Draft</option>
                    <option value="Confirmed">Confirmed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Travelers */}
          {activeStep === 2 && (
            <div className="form-step">
              <h3 className="step-heading">Travelers</h3>
              <p className="step-description">Add the names of all travelers for this itinerary</p>
              
              {errors.preparedFor && (
                <div className="alert error">
                  <span className="error-text">{errors.preparedFor}</span>
                </div>
              )}
              
              <div className="array-section">
                {formData.preparedFor.map((traveler, index) => (
                  <div key={index} className="array-item">
                    <div className="array-item-content">
                      <span className="item-number">{index + 1}</span>
                      <input
                        value={traveler}
                        onChange={(e) => handleTravelerChange(index, e.target.value)}
                        placeholder="Enter traveler name"
                        disabled={isLoading}
                      />
                    </div>
                    {formData.preparedFor.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeTraveler(index)}
                        className="btn-remove"
                        disabled={isLoading}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addTraveler} 
                  className="btn-add-item"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Traveler
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Destinations */}
          {activeStep === 3 && (
            <div className="form-step">
              <h3 className="step-heading">Destinations</h3>
              <p className="step-description">Add all cities and countries you'll be visiting</p>
              
              {errors.destinations && (
                <div className="alert error">
                  <span className="error-text">{errors.destinations}</span>
                </div>
              )}
              
              <div className="destinations-grid">
                {formData.destinations.map((dest, index) => (
                  <div key={index} className="destination-card">
                    <div className="destination-card-header">
                      <h4>Destination {index + 1}</h4>
                      {formData.destinations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDestination(index)}
                          className="btn-remove-sm"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <div className="destination-form">
                      <div className="form-group">
                        <label>City <span className="required">*</span></label>
                        <input
                          value={dest.city}
                          onChange={(e) => handleDestinationChange(index, "city", e.target.value)}
                          placeholder="e.g., Lisbon"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group">
                        <label>Country <span className="required">*</span></label>
                        <input
                          value={dest.country}
                          onChange={(e) => handleDestinationChange(index, "country", e.target.value)}
                          placeholder="e.g., Portugal"
                          disabled={isLoading}
                        />
                      </div>
                      <div className="form-group full-width">
                        <label>Description (Optional)</label>
                        <textarea
                          value={dest.description}
                          onChange={(e) => handleDestinationChange(index, "description", e.target.value)}
                          placeholder="Brief description of this destination..."
                          rows="2"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addDestination} 
                  className="btn-add-destination"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Destination
                </button>
              </div>
            </div>
          )}

          {/* Step 4: Itinerary Days */}
          {activeStep === 4 && (
            <div className="form-step">
              <h3 className="step-heading">Itinerary Days</h3>
              <p className="step-description">Plan each day of your itinerary</p>
              
              <div className="days-container">
                {formData.days.map((day, dayIndex) => (
                  <div key={dayIndex} className="day-card">
                    <div className="day-card-header">
                      <h4>Day {day.dayNumber}</h4>
                      {formData.days.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDay(dayIndex)}
                          className="btn-remove-sm"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="day-form-grid">
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="date"
                          value={day.date}
                          onChange={(e) => handleDayChange(dayIndex, "date", e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>City</label>
                        <input
                          value={day.city}
                          onChange={(e) => handleDayChange(dayIndex, "city", e.target.value)}
                          placeholder="City for this day"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Day Title</label>
                        <input
                          value={day.title}
                          onChange={(e) => handleDayChange(dayIndex, "title", e.target.value)}
                          placeholder="e.g., Best of Lisbon - Full-Day Private Tour"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Description</label>
                        <textarea
                          value={day.description}
                          onChange={(e) => handleDayChange(dayIndex, "description", e.target.value)}
                          placeholder="Describe the day's activities..."
                          rows="3"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Meals Included</label>
                        <div className="meals-checkboxes">
                          {mealsOptions.map(meal => (
                            <label key={meal} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={day.mealsIncluded.includes(meal)}
                                onChange={() => handleMealToggle(dayIndex, meal)}
                                disabled={isLoading}
                              />
                              <span>{meal}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Inclusions (comma-separated)</label>
                        <input
                          value={day.inclusions.join(", ")}
                          onChange={(e) => handleDayChange(dayIndex, "inclusions", e.target.value.split(",").map(item => item.trim()))}
                          placeholder="e.g., Hotel pickup, Tour guide, Entrance fees"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Exclusions (comma-separated)</label>
                        <input
                          value={day.exclusions.join(", ")}
                          onChange={(e) => handleDayChange(dayIndex, "exclusions", e.target.value.split(",").map(item => item.trim()))}
                          placeholder="e.g., Personal expenses, Tips, Meals not mentioned"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                    
                    {/* Activities Section */}
                    <div className="activities-section">
                      <h5>Activities</h5>
                      {day.activities.map((activity, activityIndex) => (
                        <div key={activityIndex} className="activity-card">
                          <div className="activity-header">
                            <span>Activity {activityIndex + 1}</span>
                            {day.activities.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeActivity(dayIndex, activityIndex)}
                                className="btn-remove-xs"
                                disabled={isLoading}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                          
                          <div className="activity-form-grid">
                            <div className="form-group">
                              <label>Activity Name</label>
                              <input
                                value={activity.name}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "name", e.target.value)}
                                placeholder="Activity name"
                                disabled={isLoading}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>Start Time</label>
                              <input
                                type="time"
                                value={activity.startTime}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "startTime", e.target.value)}
                                disabled={isLoading}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>End Time</label>
                              <input
                                type="time"
                                value={activity.endTime}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "endTime", e.target.value)}
                                disabled={isLoading}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>Guide Name</label>
                              <input
                                value={activity.guideName}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "guideName", e.target.value)}
                                placeholder="Guide name"
                                disabled={isLoading}
                              />
                            </div>
                            
                            <div className="form-group">
                              <label>Guide Phone</label>
                              <input
                                value={activity.guidePhone}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "guidePhone", e.target.value)}
                                placeholder="Guide phone number"
                                disabled={isLoading}
                              />
                            </div>
                            
                            <div className="form-group full-width">
                              <label>Description</label>
                              <textarea
                                value={activity.description}
                                onChange={(e) => handleActivityChange(dayIndex, activityIndex, "description", e.target.value)}
                                placeholder="Activity description"
                                rows="2"
                                disabled={isLoading}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      <button 
                        type="button" 
                        onClick={() => addActivity(dayIndex)} 
                        className="btn-add-activity"
                        disabled={isLoading}
                      >
                        <Plus size={14} /> Add Activity
                      </button>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addDay} 
                  className="btn-add-day"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Day
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Flights & Hotels */}
          {activeStep === 5 && (
            <div className="form-step">
              <h3 className="step-heading">Flights & Hotels</h3>
              <p className="step-description">Add flight and accommodation details</p>
              
              {/* Flights Section */}
              <div className="section-container">
                <h4 className="section-title">
                  <Plane size={18} /> Flights
                </h4>
                
                {formData.flights.map((flight, index) => (
                  <div key={index} className="flight-card">
                    <div className="flight-card-header">
                      <h5>Flight {index + 1}</h5>
                      {formData.flights.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeFlight(index)}
                          className="btn-remove-sm"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="flight-form-grid">
                      <div className="form-group">
                        <label>Flight Number</label>
                        <input
                          value={flight.flightNumber}
                          onChange={(e) => handleFlightChange(index, "flightNumber", e.target.value)}
                          placeholder="e.g., BA245"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Airline</label>
                        <input
                          value={flight.airline}
                          onChange={(e) => handleFlightChange(index, "airline", e.target.value)}
                          placeholder="e.g., British Airways"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Operated By</label>
                        <input
                          value={flight.operatedBy}
                          onChange={(e) => handleFlightChange(index, "operatedBy", e.target.value)}
                          placeholder="Operating airline"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>From City</label>
                        <input
                          value={flight.fromCity}
                          onChange={(e) => handleFlightChange(index, "fromCity", e.target.value)}
                          placeholder="Departure city"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>To City</label>
                        <input
                          value={flight.toCity}
                          onChange={(e) => handleFlightChange(index, "toCity", e.target.value)}
                          placeholder="Arrival city"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Departure Date</label>
                        <input
                          type="date"
                          value={flight.departureDate}
                          onChange={(e) => handleFlightChange(index, "departureDate", e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Departure Time</label>
                        <input
                          type="time"
                          value={flight.departureTime}
                          onChange={(e) => handleFlightChange(index, "departureTime", e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Arrival Date</label>
                        <input
                          type="date"
                          value={flight.arrivalDate}
                          onChange={(e) => handleFlightChange(index, "arrivalDate", e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Arrival Time</label>
                        <input
                          type="time"
                          value={flight.arrivalTime}
                          onChange={(e) => handleFlightChange(index, "arrivalTime", e.target.value)}
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addFlight} 
                  className="btn-add-section"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Flight
                </button>
              </div>
              
              {/* Hotels Section */}
              <div className="section-container">
                <h4 className="section-title">
                  <Hotel size={18} /> Hotels
                </h4>
                
                {formData.hotels.map((hotel, index) => (
                  <div key={index} className="hotel-card">
                    <div className="hotel-card-header">
                      <h5>Hotel {index + 1}</h5>
                      {formData.hotels.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeHotel(index)}
                          className="btn-remove-sm"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="hotel-form-grid">
                      <div className="form-group">
                        <label>Hotel Name</label>
                        <input
                          value={hotel.name}
                          onChange={(e) => handleHotelChange(index, "name", e.target.value)}
                          placeholder="Hotel name"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Address</label>
                        <input
                          value={hotel.address}
                          onChange={(e) => handleHotelChange(index, "address", e.target.value)}
                          placeholder="Street address"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>City</label>
                        <input
                          value={hotel.city}
                          onChange={(e) => handleHotelChange(index, "city", e.target.value)}
                          placeholder="City"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Country</label>
                        <input
                          value={hotel.country}
                          onChange={(e) => handleHotelChange(index, "country", e.target.value)}
                          placeholder="Country"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          value={hotel.phone}
                          onChange={(e) => handleHotelChange(index, "phone", e.target.value)}
                          placeholder="Hotel phone number"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Hotel Images</label>
                        <div className="image-upload-container">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => handleImageUpload(index, e)}
                            className="file-input"
                            disabled={isLoading}
                          />
                          <button 
                            type="button" 
                            className="btn-upload"
                            disabled={isLoading}
                            onClick={() => document.querySelectorAll('.file-input')[index]?.click()}
                          >
                            <Plus size={14} /> Upload Images
                          </button>
                          {hotel.images.length > 0 && (
                            <div className="image-preview">
                              {hotel.images.map((img, imgIndex) => (
                                <span key={imgIndex} className="image-name">{img}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Room Details */}
                      <div className="form-group full-width">
                        <label>Room Details</label>
                        {hotel.roomDetails.map((room, roomIndex) => (
                          <div key={roomIndex} className="room-detail-row">
                            <input
                              value={room.roomType}
                              onChange={(e) => handleRoomDetailChange(index, roomIndex, "roomType", e.target.value)}
                              placeholder="Room type (e.g., Double Room)"
                              className="room-type-input"
                              disabled={isLoading}
                            />
                            <input
                              type="number"
                              value={room.nights}
                              onChange={(e) => handleRoomDetailChange(index, roomIndex, "nights", e.target.value)}
                              placeholder="Nights"
                              className="nights-input"
                              disabled={isLoading}
                            />
                            {hotel.roomDetails.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRoomDetail(index, roomIndex)}
                                className="btn-remove-xs"
                                disabled={isLoading}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        ))}
                        
                        <button 
                          type="button" 
                          onClick={() => addRoomDetail(index)} 
                          className="btn-add-room"
                          disabled={isLoading}
                        >
                          <Plus size={14} /> Add Room Type
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addHotel} 
                  className="btn-add-section"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Hotel
                </button>
              </div>
              
              {/* Local Contacts Section */}
              <div className="section-container">
                <h4 className="section-title">
                  <Phone size={18} /> Local Contacts
                </h4>
                
                {formData.localContacts.map((contact, index) => (
                  <div key={index} className="contact-card">
                    <div className="contact-card-header">
                      <h5>Contact {index + 1}</h5>
                      {formData.localContacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          className="btn-remove-sm"
                          disabled={isLoading}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    
                    <div className="contact-form-grid">
                      <div className="form-group">
                        <label>Name</label>
                        <input
                          value={contact.name}
                          onChange={(e) => handleContactChange(index, "name", e.target.value)}
                          placeholder="Contact name"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Company</label>
                        <input
                          value={contact.company}
                          onChange={(e) => handleContactChange(index, "company", e.target.value)}
                          placeholder="Company name"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Role</label>
                        <input
                          value={contact.role}
                          onChange={(e) => handleContactChange(index, "role", e.target.value)}
                          placeholder="Role/Position"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          value={contact.phone}
                          onChange={(e) => handleContactChange(index, "phone", e.target.value)}
                          placeholder="Phone number"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group">
                        <label>Email</label>
                        <input
                          type="email"
                          value={contact.email}
                          onChange={(e) => handleContactChange(index, "email", e.target.value)}
                          placeholder="Email address"
                          disabled={isLoading}
                        />
                      </div>
                      
                      <div className="form-group full-width">
                        <label>Address</label>
                        <input
                          value={contact.address}
                          onChange={(e) => handleContactChange(index, "address", e.target.value)}
                          placeholder="Full address"
                          disabled={isLoading}
                        />
                      </div>
                    </div>
                  </div>
                ))}
                
                <button 
                  type="button" 
                  onClick={addContact} 
                  className="btn-add-section"
                  disabled={isLoading}
                >
                  <Plus size={16} /> Add Another Contact
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Review */}
          {activeStep === 6 && (
            <div className="form-step">
              <h3 className="step-heading">Review & Notes</h3>
              <p className="step-description">Review your itinerary details and add any notes</p>
              
              {/* Show validation errors */}
              {(errors.title || errors.startDate || errors.endDate || errors.preparedFor || errors.destinations) && (
                <div className="alert error">
                  <p>Please fix the following errors:</p>
                  <ul>
                    {errors.title && <li>{errors.title}</li>}
                    {errors.startDate && <li>{errors.startDate}</li>}
                    {errors.endDate && <li>{errors.endDate}</li>}
                    {errors.preparedFor && <li>{errors.preparedFor}</li>}
                    {errors.destinations && <li>{errors.destinations}</li>}
                  </ul>
                </div>
              )}
              
              <div className="review-section">
                <div className="review-card">
                  <div className="review-item">
                    <span className="review-label">Title:</span>
                    <span className="review-value">{formData.title || "Not specified"}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Status:</span>
                    <span className="review-value">{formData.status}</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Dates:</span>
                    <span className="review-value">
                      {formData.startDate ? new Date(formData.startDate).toLocaleDateString() : "Not specified"} - {formData.endDate ? new Date(formData.endDate).toLocaleDateString() : "Not specified"}
                      {formData.totalDays && ` (${formData.totalDays} days)`}
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Travelers:</span>
                    <span className="review-value">
                      {formData.preparedFor.filter(t => t.trim()).join(", ") || "Not specified"}
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Destinations:</span>
                    <span className="review-value">
                      {formData.destinations
                        .filter(d => d.city.trim() && d.country.trim())
                        .map(d => `${d.city}, ${d.country}`)
                        .join("; ") || "Not specified"}
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Days Planned:</span>
                    <span className="review-value">{formData.days.length} days</span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Flights:</span>
                    <span className="review-value">
                      {formData.flights.filter(f => f.flightNumber.trim() || f.airline.trim()).length} flights
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Hotels:</span>
                    <span className="review-value">
                      {formData.hotels.filter(h => h.name.trim() || h.city.trim()).length} hotels
                    </span>
                  </div>
                  <div className="review-item">
                    <span className="review-label">Local Contacts:</span>
                    <span className="review-value">
                      {formData.localContacts.filter(c => c.name.trim() || c.company.trim()).length} contacts
                    </span>
                  </div>
                </div>
                
                <div className="form-group">
                  <label>Additional Notes</label>
                  <textarea
                    name="notes"
                    rows="4"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Any special notes, requirements, or additional information for this itinerary..."
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <div className="action-left">
              {activeStep > 1 && (
                <button 
                  type="button" 
                  className="btn-outline" 
                  onClick={handlePrevStep}
                  disabled={isLoading}
                >
                  Back
                </button>
              )}
            </div>
            <div className="action-right">
              {activeStep < 6 ? (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={handleNextStep}
                  disabled={isLoading}
                >
                  Next Step <ChevronRight size={16} />
                </button>
              ) : (
                <button 
                  type="submit" 
                  className="btn-primary" 
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader size={16} className="spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Itinerary"
                  )}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}